import { parseFromValuesOrFunc } from 'blocks/cx-data-table/utils/utils'
import clsx from 'clsx'
import type { DataTableInstance, DataTableRowData } from '../../types'
import { ColumnVisibilityToggleButton } from '../buttons/show-hide-columns-button'
import { ClientSideFilter } from '../inputs/client-side-filter'
import { GlobalFilterTextInput } from '../inputs/global-filter-textfield'
import { ToolbarInternalButtons } from './toolbar-internal-buttons'

interface Props<TData extends DataTableRowData> {
  table: DataTableInstance<TData>
}

export const DataTableTopToolbar = <TData extends DataTableRowData>({
  table,
  ...rest
}: Props<TData>) => {
  const {
    getState,
    options: {
      enableGlobalFilter,
      enableToolbarInternalActions,
      positionGlobalFilter,
      enableColumnOrdering,
      enableColumnPinning,
      enableHiding,
      enableColumnFilters,
      cxTableTopToolbarProps,
      renderTopToolbarCustomActions,
      renderCustomFilter,
      renderCustomLeftSection,
    },
  } = table

  const { showGlobalFilter } = getState()

  const toolbarProps = {
    ...parseFromValuesOrFunc(cxTableTopToolbarProps, { table }),
    ...rest,
  }

  const stackAlertBanner = !!renderTopToolbarCustomActions || showGlobalFilter

  const globalFilterProps = {
    style: {
      zIndex: 3,
    },
    table,
  }

  const hasAnyToolbarFeatures = [
    enableHiding,
    enableColumnOrdering,
    enableColumnPinning,
    enableColumnFilters,
    enableGlobalFilter && positionGlobalFilter === 'left',
  ].some(Boolean)

  return (
    <div
      {...toolbarProps}
      className={clsx(
        'items-start grid flex-wrap-reverse  overflow-visible p-0 transition-all duration-150 ease-in-out z-[3] bg-background relative',
        toolbarProps?.className,
        hasAnyToolbarFeatures ? 'min-h-[3.5rem]' : 'min-h-0',
      )}
    >
      <div
        className={clsx(
          'flex',
          'w-full box-border flex items-start justify-between p-0',
          stackAlertBanner && 'relative',
        )}
      >
        <div className="flex gap-2 items-center flex-1">
          {enableGlobalFilter && positionGlobalFilter === 'left' && (
            <GlobalFilterTextInput {...globalFilterProps} />
          )}
          {renderCustomFilter?.({ table })}
          {(enableHiding || enableColumnOrdering || enableColumnPinning) && (
            <ColumnVisibilityToggleButton table={table} />
          )}
          {renderCustomLeftSection?.({ table })}
          {enableColumnFilters && <ClientSideFilter table={table} />}

          {renderTopToolbarCustomActions?.({ table }) ?? <span />}
        </div>

        {enableToolbarInternalActions ? (
          <div className="flex justify-end flex-wrap-reverse">
            {enableGlobalFilter && positionGlobalFilter === 'right' && (
              <GlobalFilterTextInput {...globalFilterProps} />
            )}
            <ToolbarInternalButtons table={table} />
          </div>
        ) : (
          enableGlobalFilter &&
          positionGlobalFilter === 'right' && <GlobalFilterTextInput {...globalFilterProps} />
        )}
      </div>
    </div>
  )
}
